import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Bell, BookOpen, User, Target, Brain, Briefcase,
  CheckCircle, ArrowRight, Sparkles, Lock
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const YAS_LOGO = '/yas-logo.png';

const OnboardingFlow = ({ user, setUser, onComplete }) => {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const [status, setStatus] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [pendingAnnouncements, setPendingAnnouncements] = useState([]);
  const [currentAnnouncementIndex, setCurrentAnnouncementIndex] = useState(0);
  const [faqs, setFaqs] = useState([]);
  const [faqScrolled, setFaqScrolled] = useState(false);

  const steps = [
    { id: 'announcements', title: 'View Announcements', icon: Bell, required: true },
    { id: 'faq', title: 'Read FAQ', icon: BookOpen, required: true },
    { id: 'profile', title: 'Complete Profile', icon: User, required: true },
    { id: 'goals', title: 'Set Goals', icon: Target, required: true },
    { id: 'assessment', title: 'Skills Tracker', icon: Brain, required: false },
    { id: 'project', title: 'Summer Project', icon: Briefcase, required: false }
  ];

  useEffect(() => {
    fetchOnboardingStatus();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchOnboardingStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const [statusRes, faqRes] = await Promise.all([
        fetch(`${API_URL}/api/onboarding/status`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/faq`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      if (statusRes.ok) {
        const data = await statusRes.json();
        setStatus(data);
        setPendingAnnouncements(data.pending_announcements || []);
        
        // Determine current step based on status
        if (!data.steps.view_announcements && data.pending_announcements?.length > 0) setCurrentStep(0);
        else if (!data.steps.read_faq) setCurrentStep(1);
        else if (!data.steps.complete_profile) setCurrentStep(2);
        else if (!data.steps.set_goals) setCurrentStep(3);
        else if (!data.steps.pre_assessment) setCurrentStep(4);
        else if (!data.steps.select_project) setCurrentStep(5);
        else if (onComplete) onComplete();
      }

      if (faqRes.ok) {
        const faqData = await faqRes.json();
        setFaqs(faqData.length > 0 ? faqData : defaultFAQs);
      } else {
        setFaqs(defaultFAQs);
      }
    } catch (error) {
      console.error('Error:', error);
      setFaqs(defaultFAQs);
    }
    setLoading(false);
  };

  const defaultFAQs = [
    { question: "How do I set up direct deposit?", answer: "Visit the HR office with your banking information or use the payroll portal link sent to your email." },
    { question: "What if I'm running late to work?", answer: "Contact your supervisor immediately via phone or text. Always communicate proactively." },
    { question: "How do timesheets work?", answer: "Log your hours daily in the timesheet system. Submit by Friday each week for accurate payment." },
    { question: "What should I wear to work?", answer: "Dress code varies by worksite. When in doubt, business casual is a safe choice. Ask your supervisor." },
    { question: "Who do I contact about my paycheck?", answer: "Contact the SYEP payroll department at payroll@syep.gov or call (555) 123-4567." },
    { question: "What are the program hours?", answer: "Standard hours are 9 AM - 3 PM, Monday through Friday. Check with your specific worksite for variations." }
  ];

  const acknowledgeAnnouncement = async (announcementId) => {
    try {
      const res = await fetch(`${API_URL}/api/announcements/${announcementId}/acknowledge`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      if (res.ok) {
        if (currentAnnouncementIndex < pendingAnnouncements.length - 1) {
          setCurrentAnnouncementIndex(prev => prev + 1);
        } else {
          toast.success('All announcements acknowledged!');
          setCurrentStep(1);
          fetchOnboardingStatus();
        }
      }
    } catch (error) {
      toast.error('Failed to acknowledge');
    }
  };

  const markFaqRead = async () => {
    try {
      const res = await fetch(`${API_URL}/api/onboarding/mark-faq-read`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      if (res.ok) {
        toast.success('FAQ completed!');
        setCurrentStep(2);
        fetchOnboardingStatus();
      }
    } catch (error) {
      toast.error('Failed to save progress');
    }
  };

  const handleFaqScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollTop + clientHeight >= scrollHeight - 20) {
      setFaqScrolled(true);
    }
  };

  const progress = status ? (status.completed_steps / 4) * 100 : 0; // 4 mandatory steps

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: colors.background }}>
        <div className="text-2xl font-heading font-bold text-primary">Loading...</div>
      </div>
    );
  }

  // Check if mandatory onboarding is complete
  if (status?.onboarding_completed && currentStep >= 4) {
    return null; // Don't show onboarding if complete
  }

  return (
    <div className="min-h-screen p-4 md:p-8" style={{ backgroundColor: colors.background }}>
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <img src={YAS_LOGO} alt="SYEP" className="w-20 h-20 mx-auto mb-4 object-contain" />
          <h1 className="font-heading text-3xl font-bold text-gray-900 mb-2">
            Welcome to YAS Connect! 🎉
          </h1>
          <p className="text-muted-foreground">Complete these steps to unlock the full platform</p>
        </div>

        {/* Progress */}
        <Card className="p-4 mb-6 rounded-2xl">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold">Onboarding Progress</span>
            <span className="text-sm font-bold text-primary">{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-3" />
        </Card>

        {/* Steps Overview */}
        <div className="grid grid-cols-6 gap-2 mb-8">
          {steps.map((step, idx) => {
            const isComplete = status?.steps[step.id === 'announcements' ? 'view_announcements' : step.id === 'faq' ? 'read_faq' : step.id === 'profile' ? 'complete_profile' : step.id === 'goals' ? 'set_goals' : step.id === 'assessment' ? 'pre_assessment' : 'select_project'];
            const isCurrent = idx === currentStep;
            const isLocked = idx > currentStep && !isComplete;

            return (
              <div
                key={step.id}
                className={`flex flex-col items-center p-2 rounded-xl transition-all ${
                  isComplete ? 'bg-green-100' : isCurrent ? 'bg-primary/10' : 'bg-gray-100'
                }`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center mb-1 ${
                  isComplete ? 'bg-green-500 text-white' : isCurrent ? 'bg-primary text-white' : 'bg-gray-300'
                }`}>
                  {isComplete ? <CheckCircle className="w-4 h-4" /> : isLocked ? <Lock className="w-4 h-4" /> : <step.icon className="w-4 h-4" />}
                </div>
                <span className="text-[10px] text-center font-medium">{step.title}</span>
              </div>
            );
          })}
        </div>

        {/* Current Step Content */}
        <AnimatePresence mode="wait">
          {/* Step 0: Announcements */}
          {currentStep === 0 && pendingAnnouncements.length > 0 && (
            <motion.div
              key="announcements"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Card className="p-6 rounded-3xl">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-red-100 rounded-2xl flex items-center justify-center">
                    <Bell className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <h2 className="font-heading text-xl font-bold">Important Announcement</h2>
                    <p className="text-sm text-muted-foreground">
                      {currentAnnouncementIndex + 1} of {pendingAnnouncements.length}
                    </p>
                  </div>
                </div>

                <div className={`p-4 rounded-2xl mb-4 ${
                  pendingAnnouncements[currentAnnouncementIndex]?.category === 'urgent' 
                    ? 'bg-red-50 border-2 border-red-200' 
                    : 'bg-blue-50'
                }`}>
                  <h3 className="font-bold text-lg mb-2">
                    {pendingAnnouncements[currentAnnouncementIndex]?.title}
                  </h3>
                  <p className="text-muted-foreground">
                    {pendingAnnouncements[currentAnnouncementIndex]?.content}
                  </p>
                </div>

                <Button
                  onClick={() => acknowledgeAnnouncement(pendingAnnouncements[currentAnnouncementIndex]?.id)}
                  className="w-full h-12 rounded-full"
                >
                  <CheckCircle className="w-5 h-5 mr-2" />
                  I have read and understood
                </Button>
              </Card>
            </motion.div>
          )}

          {/* Step 0: No announcements */}
          {currentStep === 0 && pendingAnnouncements.length === 0 && (
            <motion.div
              key="no-announcements"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Card className="p-6 rounded-3xl text-center">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h2 className="font-heading text-xl font-bold mb-2">No Pending Announcements</h2>
                <p className="text-muted-foreground mb-4">You're all caught up!</p>
                <Button onClick={() => setCurrentStep(1)} className="rounded-full">
                  Continue to FAQ <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Card>
            </motion.div>
          )}

          {/* Step 1: FAQ */}
          {currentStep === 1 && (
            <motion.div
              key="faq"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Card className="p-6 rounded-3xl">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center">
                    <BookOpen className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="font-heading text-xl font-bold">Frequently Asked Questions</h2>
                    <p className="text-sm text-muted-foreground">Scroll to read all FAQs</p>
                  </div>
                </div>

                <div 
                  className="max-h-[400px] overflow-y-auto space-y-4 mb-4 pr-2"
                  onScroll={handleFaqScroll}
                >
                  {faqs.map((faq, idx) => (
                    <div key={idx} className="bg-gray-50 rounded-2xl p-4">
                      <h3 className="font-bold mb-2">{faq.question}</h3>
                      <p className="text-muted-foreground text-sm">{faq.answer}</p>
                    </div>
                  ))}
                </div>

                <Button
                  onClick={markFaqRead}
                  disabled={!faqScrolled && faqs.length > 3}
                  className="w-full h-12 rounded-full"
                >
                  {faqScrolled || faqs.length <= 3 ? (
                    <>
                      <CheckCircle className="w-5 h-5 mr-2" />
                      I have read the FAQ
                    </>
                  ) : (
                    'Scroll to read all FAQs'
                  )}
                </Button>
              </Card>
            </motion.div>
          )}

          {/* Step 2: Profile */}
          {currentStep === 2 && (
            <motion.div
              key="profile"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Card className="p-6 rounded-3xl text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <User className="w-8 h-8 text-purple-600" />
                </div>
                <h2 className="font-heading text-xl font-bold mb-2">Complete Your Profile</h2>
                <p className="text-muted-foreground mb-4">
                  Tell us about yourself - your interests, school, and favorites
                </p>
                <Button onClick={() => navigate('/profile')} className="rounded-full">
                  Go to Profile <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Card>
            </motion.div>
          )}

          {/* Step 3: Goals */}
          {currentStep === 3 && (
            <motion.div
              key="goals"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Card className="p-6 rounded-3xl text-center">
                <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Target className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="font-heading text-xl font-bold mb-2">Set Your Goals</h2>
                <p className="text-muted-foreground mb-4">
                  Set at least 2 SMART goals for this summer
                </p>
                <Button onClick={() => navigate('/goals')} className="rounded-full">
                  Set Goals <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Card>
            </motion.div>
          )}

          {/* Step 4: Assessment (Optional) */}
          {currentStep === 4 && (
            <motion.div
              key="assessment"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Card className="p-6 rounded-3xl text-center">
                <div className="w-16 h-16 bg-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Brain className="w-8 h-8 text-indigo-600" />
                </div>
                <h2 className="font-heading text-xl font-bold mb-2">Skills Assessment</h2>
                <p className="text-muted-foreground mb-4">
                  Complete the pre-assessment to track your growth (50 points!)
                </p>
                <div className="flex gap-3 justify-center">
                  <Button variant="outline" onClick={() => setCurrentStep(5)} className="rounded-full">
                    Skip for Now
                  </Button>
                  <Button onClick={() => navigate('/assessment')} className="rounded-full">
                    Take Assessment <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </Card>
            </motion.div>
          )}

          {/* Step 5: Project (Optional) */}
          {currentStep === 5 && (
            <motion.div
              key="project"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Card className="p-6 rounded-3xl text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Briefcase className="w-8 h-8 text-orange-600" />
                </div>
                <h2 className="font-heading text-xl font-bold mb-2">Choose Your Summer Project</h2>
                <p className="text-muted-foreground mb-4">
                  Select a project to work on throughout the summer (20 points!)
                </p>
                <div className="flex gap-3 justify-center">
                  <Button variant="outline" onClick={() => onComplete && onComplete()} className="rounded-full">
                    Skip for Now
                  </Button>
                  <Button onClick={() => navigate('/summer-project')} className="rounded-full">
                    Choose Project <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Skip to Dashboard (if mandatory complete) */}
        {status?.onboarding_completed && currentStep >= 4 && (
          <div className="text-center mt-6">
            <Button
              variant="outline"
              onClick={() => navigate('/dashboard')}
              className="rounded-full"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Go to Dashboard
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default OnboardingFlow;
